import {useCallback, useEffect, useRef, useState} from 'react';

import {webhookDeliveriesService} from '@/services/webhookDeliveriesService';
import type {
  WebhookDelivery,
  WebhookDeliveryStatus,
} from '@/types/WebhookDelivery';
import {logRejection} from '@/utils/logRejection';

const DELIVERIES_PAGE_LIMIT = 10;

export type WebhookDeliveryFilters = {
  event?: string;
  status?: WebhookDeliveryStatus;
};

// Lists a business's webhook deliveries with cursor-based "load more" paging and
// event/status filtering.
//
//   setFilters() ── reset ──> fetchPage(true)  ── replaces rows, sets cursor
//   loadMore()   ── append ─> fetchPage(false) ── appends rows, advances cursor
//
export default function useWebhookDeliveries(businessId: string) {
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [filters, setFiltersState] = useState<WebhookDeliveryFilters>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const requestSeq = useRef(0);
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchPage = useCallback(
    async (reset: boolean, nextCursor?: string) => {
      const seq = ++requestSeq.current;
      setIsLoading(true);
      setError(null);
      try {
        const {data, cursor: returnedCursor} =
          await webhookDeliveriesService.getBusinessWebhookDeliveries(
            businessId,
            {
              limit: DELIVERIES_PAGE_LIMIT,
              cursor: reset ? undefined : nextCursor,
              event: filters.event,
              status: filters.status,
            },
          );
        // Ignore stale / superseded responses.
        if (!isMounted.current || seq !== requestSeq.current) {
          return;
        }
        setDeliveries((prev) => (reset ? data : [...prev, ...data]));
        setCursor(returnedCursor);
      } catch (err) {
        if (!isMounted.current || seq !== requestSeq.current) {
          return;
        }
        console.error('Failed to fetch webhook deliveries:', err);
        setError(err as Error);
      } finally {
        if (isMounted.current && seq === requestSeq.current) {
          setIsLoading(false);
        }
      }
    },
    [businessId, filters.event, filters.status],
  );

  // (Re)load the first page on mount and whenever businessId/filters change.
  useEffect(() => {
    // `fetchPage` catches into `error` state, so these handlers only fire if
    // that catch or the finally itself throws. Log rather than swallow.
    fetchPage(true).catch(logRejection('Failed to load webhook deliveries'));
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (cursor) {
      fetchPage(false, cursor).catch(
        logRejection('Failed to load more webhook deliveries'),
      );
    }
  }, [cursor, fetchPage]);

  const setFilters = useCallback((next: WebhookDeliveryFilters) => {
    setFiltersState(next);
  }, []);

  return {
    deliveries,
    cursor,
    hasMore: Boolean(cursor),
    isLoading,
    error,
    filters,
    setFilters,
    loadMore,
  };
}
